// SHADOW MARKET™ — Kids achievements / trophies page.
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Star, Trophy } from "lucide-react";
import { ASSETS } from "../assets";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";
import { LECCIONES_KIDS } from "../data/kidsLecciones";
import { getKidsUi } from "../i18n/kids-academy";
import { getKidsProgress } from "../utils/kidsProgress";
import { Personaje, PERSONAJE_COLORS } from "../components/kids/Personaje";

const KidsLogrosPage = () => {
  const { lang } = useLanguage();
  const ui = getKidsUi(lang);
  const progress = getKidsProgress();
  const completed = LECCIONES_KIDS.filter((l) => progress[l.id]?.completed);
  const totalStars = LECCIONES_KIDS.reduce((acc, l) => acc + (progress[l.id]?.stars || 0), 0);
  const maxStars = LECCIONES_KIDS.length * 3;

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-black text-white">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(245,166,35,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(155,89,255,0.12),transparent_55%)]" />
      </div>

      <header className="relative z-20 border-b border-white/5 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 md:px-10 md:py-4">
          <Link
            to="/kids"
            className="inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white"
            data-testid="kids-logros-back"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">{ui.back_to_map}</span>
          </Link>
          <img src={ASSETS.logos.rebel} alt="REBEL" className="h-8 w-auto md:h-10" />
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-4xl px-4 py-10 md:px-10">
        <div className="mb-8 text-center">
          <Trophy className="mx-auto h-12 w-12 text-amber-300" />
          <h1
            className="mt-2 font-display text-3xl font-black tracking-tight md:text-5xl"
            data-testid="kids-logros-title"
            style={{
              background: "linear-gradient(135deg, #f5a623 0%, #fbbf24 50%, #f97316 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {ui.achievements}
          </h1>
          <p className="mt-2 text-sm text-white/65">{ui.achievements_sub}</p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-sm font-bold text-amber-300">
            ⭐ {totalStars} / {maxStars}
          </div>
        </div>

        {completed.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-10 text-center backdrop-blur-xl">
            <div className="mb-3 text-5xl">🔓</div>
            <p className="text-white/65">{ui.no_achievements}</p>
            <Link
              to="/kids"
              className="mt-4 inline-flex rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-2 text-xs font-black uppercase tracking-widest text-black"
            >
              ▶ {ui.start}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {completed.map((l) => {
              const stars = progress[l.id]?.stars || 1;
              const c = PERSONAJE_COLORS[l.personaje] || PERSONAJE_COLORS.satoshi;
              const titulo = l.titulo[lang] || l.titulo.es;
              return (
                <div
                  key={l.id}
                  className="rounded-2xl border p-5 backdrop-blur-xl"
                  style={{ borderColor: c.acento + "44", background: `${c.acento}0d` }}
                  data-testid={`kids-trophy-${l.id}`}
                >
                  <div className="flex items-center gap-4">
                    <Personaje id={l.personaje} estado="feliz" size={70} />
                    <div className="flex-1">
                      <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: c.acento }}>
                        {ui.level} {l.nivel}
                      </div>
                      <div className="text-base font-black text-white">{titulo}</div>
                      <div className="mt-1 flex items-center gap-1">
                        {[1, 2, 3].map((s) => (
                          <Star
                            key={s}
                            className="h-4 w-4 text-amber-300"
                            fill={s <= stars ? "currentColor" : "transparent"}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default KidsLogrosPage;
